import React from 'react';
import { ArrowLeft, FileText, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { STRIPE_CONFIG } from '../config/stripe';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Home
            </button>
            <div className="flex items-center gap-3">
              <FileText className="h-6 w-6 text-blue-600" />
              <h1 className="text-2xl font-semibold text-gray-900">
                Terms of Service
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm border p-8">
          <div className="prose prose-gray max-w-none">
            <div className="text-sm text-gray-500 mb-6">
              <strong>Last Updated:</strong> September 28, 2025
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              1. Acceptance of Terms
            </h2>
            <p className="mb-6">
              By accessing and using Briefed (&ldquo;Service&rdquo;), operated
              by SagaSwipe LLC (&ldquo;Company,&rdquo; &ldquo;we,&rdquo;
              &ldquo;us,&rdquo; or &ldquo;our&rdquo;), you accept and agree to
              be bound by the terms and provision of this agreement. If you do
              not agree to abide by the above, please do not use this service.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              2. Description of Service
            </h2>
            <p className="mb-6">
              Briefed is a legal research platform that provides access to court
              case data, docket information, and legal analytics. The Service is
              designed for legal professionals, including attorneys, paralegals,
              legal researchers, journalists, and other authorized users.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              3. User Accounts and Registration
            </h2>
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              3.1 Account Creation
            </h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                You must provide accurate, current, and complete information
                during registration
              </li>
              <li>You must maintain and update your account information</li>
              <li>
                You are responsible for maintaining the confidentiality of your
                account credentials
              </li>
              <li>
                You must notify us immediately of any unauthorized use of your
                account
              </li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              3.2 Professional Verification
            </h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                Users must provide professional information including role,
                organization, and contact details
              </li>
              <li>
                Attorneys may provide bar number and state for verification
                purposes
              </li>
              <li>
                We reserve the right to verify user credentials and suspend
                accounts that cannot be verified
              </li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              4. Subscription and Payment
            </h2>
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              4.1 Subscription Plans
            </h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                Monthly Plan: ${STRIPE_CONFIG.plans.monthly.amount / 100} per
                month
              </li>
              <li>
                Annual Plan: ${STRIPE_CONFIG.plans.annual.amount / 100} per year
                (equivalent to $
                {Math.round(
                  (STRIPE_CONFIG.plans.annual.amount / 100 / 12) * 100
                ) / 100}{' '}
                per month)
              </li>
              <li>All plans require a valid credit card for billing</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              4.2 Free Trial
            </h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>New users may receive a free trial period</li>
              <li>Credit card information is required to start a free trial</li>
              <li>
                Trial users may cancel at any time during the trial period
              </li>
              <li>
                If not cancelled before trial ends, subscription will
                automatically begin
              </li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              5. Acceptable Use
            </h2>
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              5.1 Permitted Use
            </h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                Use the Service for legitimate legal research and professional
                purposes
              </li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Respect the intellectual property rights of others</li>
              <li>Maintain the confidentiality of sensitive information</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              5.2 Prohibited Use
            </h3>
            <p className="mb-3">You may not:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                Use the Service for any unlawful purpose or in violation of any
                applicable law
              </li>
              <li>
                Attempt to gain unauthorized access to the Service or related
                systems
              </li>
              <li>
                Use automated tools to scrape, harvest, or extract data from the
                Service
              </li>
              <li>Share your account credentials with others</li>
              <li>Use the Service to compete with or replicate our business</li>
              <li>Reverse engineer, decompile, or disassemble the Service</li>
              <li>Upload malicious code or attempt to disrupt the Service</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              6. Disclaimers and Limitations
            </h2>
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              6.1 Service Availability
            </h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                The Service is provided &ldquo;as is&rdquo; and &ldquo;as
                available&rdquo;
              </li>
              <li>We do not guarantee uninterrupted or error-free service</li>
              <li>
                We may modify, suspend, or discontinue the Service at any time
              </li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              6.2 Data Accuracy
            </h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                While we strive for accuracy, we cannot guarantee the
                completeness or accuracy of all data
              </li>
              <li>Users should verify important information independently</li>
              <li>
                We are not responsible for decisions made based on Service data
              </li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              6.3 Legal Advice
            </h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                The Service provides data and research tools, not legal advice
              </li>
              <li>
                Users should consult qualified legal professionals for legal
                advice
              </li>
              <li>
                We are not responsible for legal outcomes based on Service usage
              </li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              7. Limitation of Liability
            </h2>
            <p className="mb-6">
              To the maximum extent permitted by law, our total liability to you
              for any claims arising from these terms or the Service shall not
              exceed the amount you paid us in the 12 months preceding the
              claim. We shall not be liable for any indirect, incidental,
              special, consequential, or punitive damages.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              8. Governing Law and Disputes
            </h2>
            <p className="mb-6">
              These terms shall be governed by and construed in accordance with
              the laws of the State of California, without regard to conflict of
              law principles. Any disputes arising from these terms or the
              Service shall be resolved through binding arbitration in Los
              Angeles County, California.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              9. Contact Information
            </h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <Mail className="h-6 w-6 text-gray-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    SagaSwipe LLC
                  </h3>
                  <p className="text-gray-700 mb-1">1240 Morningside Way</p>
                  <p className="text-gray-700 mb-1">Venice, CA 90291</p>
                  <p className="text-gray-700">
                    Email:{' '}
                    <a
                      href="mailto:support@sagaswipe.com"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      support@sagaswipe.com
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                <em>
                  These Terms of Service constitute a legally binding agreement
                  between you and SagaSwipe LLC regarding your use of the
                  Briefed service.
                </em>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
